import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";

function TiersAdd({ onClose, onSave, initialTiers = [] }) {
  const [tiers, setTiers] = useState([
    { id: 1, name: "Gold" },
    { id: 2, name: "Silver" },
    { id: 3, name: "Bronze" },
  ]);

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [saving, setSaving] = useState(false);

  // Initialize with provided tiers or defaults
  useEffect(() => {
    if (initialTiers && initialTiers.length > 0) {
      setTiers(
        initialTiers.map((tier, index) => ({
          id: index + 1,
          name: tier.name || tier,
        }))
      );
    }
  }, [initialTiers]);

  const handleTierChange = (id, value) => {
    setTiers((prevTiers) =>
      prevTiers.map((tier) =>
        tier.id === id ? { ...tier, name: value } : tier
      )
    );
  };

  const handleAddTier = () => {
    const newId = Math.max(...tiers.map((t) => t.id), 0) + 1;
    setTiers((prevTiers) => [...prevTiers, { id: newId, name: "" }]);
  };

  const handleDeleteTier = (id) => {
    if (tiers.length <= 1) {
      setAlert({
        open: true,
        message: "At least one tier is required",
        severity: "warning",
      });
      return;
    }

    setTiers((prevTiers) => prevTiers.filter((tier) => tier.id !== id));
  };

  const handleSubmit = async () => {
    setSaving(true);

    // Validate that all tiers have names
    const emptyTiers = tiers.filter((tier) => !tier.name.trim());
    if (emptyTiers.length > 0) {
      setAlert({
        open: true,
        message: "Please provide names for all tiers",
        severity: "error",
      });
      setSaving(false);
      return;
    }

    // Check for duplicate names
    const tierNames = tiers.map((tier) => tier.name.trim().toLowerCase());
    const uniqueNames = [...new Set(tierNames)];
    if (tierNames.length !== uniqueNames.length) {
      setAlert({
        open: true,
        message: "Tier names must be unique",
        severity: "error",
      });
      setSaving(false);
      return;
    }

    try {
      // Save tiers
      if (onSave) {
        await onSave(tiers);
      }

      setAlert({
        open: true,
        message: "Sponsor tiers saved successfully!",
        severity: "success",
      });

      // Close after a delay
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (error) {
      console.error("Error in TiersAdd:", error);
      setAlert({
        open: true,
        message: "Failed to save tiers. Please try again.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <Box sx={{ width: 450 }} role="presentation">
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ margin: "15px", paddingBottom: "10px" }}>
          <Typography variant="h4" gutterBottom>
            Manage Sponsor Tiers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and organize sponsor tiers for your event
          </Typography>
        </Grid>
      </Grid>

      <Box style={{ margin: "15px", paddingBottom: "10px" }}>
        {tiers.map((tier, index) => (
          <Grid
            container
            spacing={2}
            key={tier.id}
            sx={{ mb: 2, alignItems: "center" }}
          >
            <Grid item xs={1}>
              <Typography variant="body1" color="text.secondary">
                {index + 1}
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                label="Tier Name *"
                variant="outlined"
                fullWidth
                value={tier.name}
                onChange={(e) => handleTierChange(tier.id, e.target.value)}
                placeholder={`Enter tier ${index + 1} name`}
              />
            </Grid>
            <Grid
              item
              xs={2}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <IconButton
                color="error"
                onClick={() => handleDeleteTier(tier.id)}
                disabled={tiers.length <= 1}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Grid container justifyContent="center" sx={{ mt: 3, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleAddTier}
            color="primary"
            startIcon={<AddIcon />}
          >
            Add Tier
          </Button>
        </Grid>
      </Box>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Tiers"}
        </Button>
      </DialogActions>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
export default TiersAdd;
